import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { IdentifierType } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { NotificationQueries } from '@frontend/api-notifications';
import { UsersQueries } from '@frontend/api-users';
import { InfinitePaginatedList } from '@frontend/components';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { Link, type AnyRoute } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Accordion, TextLink, useModalControl, useAlert, SkeletonLoader } from '@frontend/design-system';
import { useCheckPaymentsEnabled, useSchedulingLocationInfo } from '../../../../../../hooks';
import { useScheduleBookingRequestsShallowStore } from '../../../../../../stores/use-schedule-booking-requests-store';
import { ScheduleRequestContextProvider } from '../Context/ScheduleRequestContext';
import { ScheduleRequestModalContextType } from '../Context/ScheduleRequestModalContext';
import { useCombinedScheduleRequest } from '../hooks/use-combined-requests';
import { useGetScheduleRequestInfiniteListQuery } from '../hooks/useGetScheduleRequestInfiniteListQuery';
import { ScheduleRequestTray } from '../ScheduleRequestModal/ScheduleRequestWrappers';
import { ScheduleRequestsTrackingIds } from '../trackingIds';
import { ScheduleRequestDetails } from './ScheduleRequestDetails';
import { ScheduleRequestFormManager } from './ScheduleRequestFormManager';
import { ScheduleRequestListItemHeader } from './ScheduleRequestListItemHeader';

interface ScheduleRequestListProps {
  locationId: string;
  height?: number | string;
}

const FOOTER_HEIGHT = 53;

export const ScheduleRequestList = ({ locationId, height }: ScheduleRequestListProps) => {
  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarActions');
  const markAsReadMutation = NotificationQueries.useMarkNotificationsAsRead();

  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);

  const [selectedScheduleRequest, setSelectedScheduleRequest] = useState<ScheduleRequest | null>(null);
  const scheduleRequestModalProps = useModalControl();

  const { isMultiLocation } = useSchedulingLocationInfo();
  const { data: locationUsers } = UsersQueries.useGetUsers({
    params: isMultiLocation ? { showChildren: true } : {},
    opts: {
      cacheTime: 10 * 60 * 1000, // 10 mins
      staleTime: 10 * 60 * 1000, // 10 mins
    },
  });

  const { combineBookingRequests } = useScheduleBookingRequestsShallowStore('combineBookingRequests');
  const combinedBookingListQuery = useCombinedScheduleRequest(locationId, combineBookingRequests);
  const scheduleRequestListQuery = useGetScheduleRequestInfiniteListQuery(locationId, combineBookingRequests);

  const { isPaymentsEnabled } = useCheckPaymentsEnabled(locationId);

  const providerValue = useMemo(
    () => ({
      locationUsers,
      refetchScheduleRequestList: combineBookingRequests
        ? () => combinedBookingListQuery.refetchQueries()
        : () => scheduleRequestListQuery.refetch(),
    }),
    [locationUsers, combinedBookingListQuery.refetchQueries, scheduleRequestListQuery.refetch]
  );

  const handleCloseScheduleRequestModal = useCallback(() => {
    scheduleRequestModalProps.closeModal();
    setTimeout(() => {
      setSelectedScheduleRequest(null);
    }, 200); // Delay to allow modal's exit animation to finish
  }, [scheduleRequestModalProps]);

  const scheduleRequestModalContextValue = useMemo<ScheduleRequestModalContextType>(
    () => ({
      scheduleRequestModalProps,
      selectedScheduleRequest: selectedScheduleRequest as ScheduleRequest,
      handleCloseScheduleRequestModal,
      refetchScheduleRequestList: () =>
        combineBookingRequests ? combinedBookingListQuery.refetchQueries() : scheduleRequestListQuery.refetch(),
      isPaymentsEnabled,
    }),
    [
      selectedScheduleRequest,
      combinedBookingListQuery.refetchQueries,
      scheduleRequestListQuery.refetch,
      isPaymentsEnabled,
    ]
  );

  const markNotificationsAsRead = (requestId: string) => {
    //Mark notification as Read for all the users on the location
    markAsReadMutation.mutate({
      notificationType: NotificationType.SCHEDULE_REQUEST,
      identifiers: [
        {
          key: 'id',
          val: requestId,
          type: IdentifierType.STRING,
        },
      ],
    });
  };

  useEffect(() => {
    if (scheduleRequestListQuery.isError) {
      alert.error(t('Failed to load schedule request list'));
    }
  }, [scheduleRequestListQuery.isError]);

  const isEmptyScheduleRequestList = combineBookingRequests
    ? combinedBookingListQuery?.data?.pages?.[0]?.rows?.length
    : !scheduleRequestListQuery?.data?.pages?.[0]?.rows?.length;

  return (
    <>
      <ScheduleRequestContextProvider value={providerValue}>
        <Accordion
          css={{ ...(isEmptyScheduleRequestList && { background: theme.colors.white }), flexGrow: 1 }}
          variant='blank'
          chevronSize={16}
          startingValue=''
          size='large'
          onChange={(val) => {
            if (!!val) {
              markNotificationsAsRead(val);
            }
          }}
        >
          <InfinitePaginatedList
            infiniteQueryProps={combineBookingRequests ? combinedBookingListQuery : scheduleRequestListQuery}
            height={typeof height === 'number' ? height - FOOTER_HEIGHT : height}
            skeletonLoader={
              <SkeletonLoader
                height={50}
                width={`calc(100% - ${theme.spacing(4)})`}
                css={{ margin: theme.spacing(2) }}
              />
            }
            emptyStateConfig={{
              type: 'schedule_requests',
              description: t('No New Requests'),
              header: '',
            }}
            renderListItem={({ listItem }) => {
              return (
                <Accordion.Item
                  trackingId={ScheduleRequestsTrackingIds.requestListItemAccordion}
                  style={{ borderBottom: `1px solid ${theme.colors.neutral10}` }}
                  value={listItem?.id ?? ''}
                  key={listItem?.id ?? ''}
                >
                  <Accordion.Header
                    css={
                      isDebugModeOn && listItem.linkHash === 'v2'
                        ? { backgroundColor: theme.colors.critical10 }
                        : undefined
                    }
                  >
                    <ScheduleRequestListItemHeader
                      requestVersion={listItem.linkHash || ''}
                      name={getFullName(listItem.schedulee ?? {})}
                      personId={listItem.schedulee?.weavePersonId}
                      appointmentTime={listItem?.dateTime ?? ''}
                      personPhone={listItem?.schedulee?.phoneNumber ?? ''}
                      paidAmount={isPaymentsEnabled && listItem?.bookingAmount ? listItem?.bookingAmount : 0}
                      locationId={listItem.locationId ?? ''}
                      updateSelectedScheduleRequest={() => {
                        setSelectedScheduleRequest(listItem);
                        scheduleRequestModalProps.openModal();
                      }}
                      scheduleRequestId={listItem.id ?? ''}
                      css={{ borderBottom: `1px solid ${theme.colors.neutral40}` }}
                      markNotificationsAsRead={markNotificationsAsRead}
                    />
                  </Accordion.Header>

                  <Accordion.Body>
                    <ScheduleRequestDetails
                      requestVersion={listItem.linkHash || ''}
                      scheduleRequestId={listItem.id ?? ''}
                      locationId={listItem.locationId ?? ''}
                      isPaymentsEnabled={isPaymentsEnabled}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              );
            }}
          />
        </Accordion>
      </ScheduleRequestContextProvider>
      {selectedScheduleRequest && (
        <ScheduleRequestFormManager
          wrapper={ScheduleRequestTray}
          scheduleRequestModalContextValue={scheduleRequestModalContextValue}
          selectedScheduleRequest={selectedScheduleRequest}
        />
      )}
      <div css={viewHistoryContainerStyles}>
        <TextLink
          as={Link}
          css={{ display: 'flex', alignItems: 'center' }}
          size='medium'
          weight='bold'
          to={`/schedule/request-history?locationId=${locationId}` as AnyRoute}
          trackingId={ScheduleRequestsTrackingIds.viewHistoryBtn}
        >
          <Icon name='external-link-small' css={{ marginRight: theme.spacing(0.5) }} />
          {t('View History')}
        </TextLink>
      </div>
    </>
  );
};

const viewHistoryContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: theme.colors.white,
  borderTop: `1px solid ${theme.colors.neutral10}`,
});
